/** Pricing configuration helpers for admin UI. */

const DEFAULT_DURATION_OPTIONS = [
  { id: '1h', label: '1 hour', duration_minutes: 60, price: '', enabled: true, sort_order: 1, is_long_duration: false },
  { id: '3h', label: '3 hours', duration_minutes: 180, price: '', enabled: true, sort_order: 2, is_long_duration: false },
  { id: '6h', label: '6 hours', duration_minutes: 360, price: '', enabled: true, sort_order: 3, is_long_duration: false },
  { id: '12h', label: '12 hours', duration_minutes: 720, price: '', enabled: true, sort_order: 4, is_long_duration: false },
  { id: '24h', label: '24 hours', duration_minutes: 1440, price: '', enabled: true, sort_order: 5, is_long_duration: false },
  { id: 'long', label: 'More than 24 hours', duration_minutes: null, price: '', enabled: true, sort_order: 6, is_long_duration: true },
];

export function isPricingComplete(service) {
  return Boolean(service?.pricing_complete);
}

export function pricingStatusLabel(service) {
  return isPricingComplete(service) ? 'Pricing complete' : 'Pricing incomplete';
}

export function pricingStatusClass(service) {
  return isPricingComplete(service)
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-amber-50 text-amber-700';
}

export function isDurationBasedService(serviceKey) {
  return ['home_nursing', 'home_care', 'bystander'].includes(serviceKey);
}

export function buildPricingPatch(form) {
  const patch = {
    currency: form.currency || 'INR',
    base_price: Number(form.base_price || 0),
    travel: {
      included_travel_km: Number(form.included_travel_km),
      travel_rate_per_km: Number(form.travel_rate_per_km),
      round_trip: Boolean(form.round_trip),
    },
    service: {
      included_duration_minutes: Number(form.included_duration_minutes),
      free_buffer_minutes: Number(form.free_buffer_minutes || 0),
    },
    extra_time: {
      billing_block_minutes: Number(form.billing_block_minutes),
      price_per_block: Number(form.price_per_block),
    },
    convenience_fee: {
      type: form.convenience_fee_type,
      value: Number(form.convenience_fee_value),
    },
    commission: {
      type: form.commission_type,
      value: Number(form.commission_value),
    },
    tax: {
      type: form.tax_type,
      value: Number(form.tax_value),
    },
  };
  if (form.service_pricing_mode) {
    patch.service_pricing_mode = form.service_pricing_mode;
  }
  if (form.booking_requirements) {
    patch.service_pricing_mode = 'duration_based';
    patch.booking_requirements = {
      case_types_enabled: form.booking_requirements.case_types_enabled || [],
      duration_options: (form.booking_requirements.duration_options || []).map((opt) => ({
        id: opt.id,
        label: opt.label,
        duration_minutes: opt.is_long_duration ? null : Number(opt.duration_minutes),
        price: opt.price === '' || opt.price == null ? null : Number(opt.price),
        enabled: Boolean(opt.enabled),
        sort_order: Number(opt.sort_order || 0),
        is_long_duration: Boolean(opt.is_long_duration),
        food_included: Boolean(opt.food_included),
        stay_included: Boolean(opt.stay_included),
      })),
      max_duration_minutes_critical: Number(form.booking_requirements.max_duration_minutes_critical || 43200),
      max_duration_minutes_non_critical: Number(form.booking_requirements.max_duration_minutes_non_critical || 43200),
      food_required_from_minutes: Number(form.booking_requirements.food_required_from_minutes || 10 ** 9),
      stay_required_from_minutes: Number(form.booking_requirements.stay_required_from_minutes || 10 ** 9),
      provider_quantity_max: 1,
    };
  }
  return patch;
}

const SERVICE_DEFAULTS = {
  home_nursing: [
    { id: '1h', label: '1 hour', duration_minutes: 60, price: 500, enabled: true, sort_order: 1, is_long_duration: false },
    { id: '2h', label: '2 hours', duration_minutes: 120, price: 900, enabled: true, sort_order: 2, is_long_duration: false },
    { id: '3h', label: '3 hours', duration_minutes: 180, price: 1300, enabled: true, sort_order: 3, is_long_duration: false },
  ],
  home_care: [
    { id: '1d', label: '1 day', duration_minutes: 1440, price: 1200, enabled: true, sort_order: 1, is_long_duration: false },
    { id: '3d', label: '3 days', duration_minutes: 4320, price: 3500, enabled: true, sort_order: 2, is_long_duration: false },
    { id: '7d', label: '1 week (7 days)', duration_minutes: 10080, price: 8000, enabled: true, sort_order: 3, is_long_duration: false },
    { id: '14d', label: '14 days', duration_minutes: 20160, price: 15500, enabled: true, sort_order: 4, is_long_duration: false },
    { id: '30d', label: '1 month', duration_minutes: 43200, price: 30000, enabled: true, sort_order: 5, is_long_duration: false },
  ],
  bystander: [
    { id: '3h', label: '3 hours', duration_minutes: 180, price: 700, enabled: true, sort_order: 1, is_long_duration: false, food_included: false, stay_included: false },
    { id: '6h', label: '6 hours', duration_minutes: 360, price: 1000, enabled: true, sort_order: 2, is_long_duration: false, food_included: true, stay_included: false },
    { id: '12h', label: '12 hours', duration_minutes: 720, price: 1500, enabled: true, sort_order: 3, is_long_duration: false, food_included: true, stay_included: false },
    { id: '24h', label: '24 hours', duration_minutes: 1440, price: 2000, enabled: true, sort_order: 4, is_long_duration: false, food_included: true, stay_included: true },
    { id: '3d', label: '3 days', duration_minutes: 4320, price: 5000, enabled: true, sort_order: 5, is_long_duration: false, food_included: true, stay_included: true },
    { id: '7d', label: '1 week', duration_minutes: 10080, price: 10000, enabled: true, sort_order: 6, is_long_duration: false, food_included: true, stay_included: true },
    { id: '14d', label: '14 days', duration_minutes: 20160, price: 18000, enabled: true, sort_order: 7, is_long_duration: false, food_included: true, stay_included: true },
  ],
};

export function pricingFormFromConfig(pricing = {}) {
  const booking = pricing.booking_requirements || {};
  const serviceDefaults = SERVICE_DEFAULTS[pricing.service_key] || DEFAULT_DURATION_OPTIONS;
  const durationOptions = (booking.duration_options || serviceDefaults).map((opt) => ({
    ...opt,
    price: opt.price ?? '',
    enabled: opt.enabled ?? true,
  }));

  return {
    currency: pricing.currency || 'INR',
    base_price: pricing.base_price ?? '',
    service_pricing_mode: pricing.service_pricing_mode || 'fixed_base',
    included_travel_km: pricing.travel?.included_travel_km ?? '',
    travel_rate_per_km: pricing.travel?.travel_rate_per_km ?? '',
    round_trip: pricing.travel?.round_trip ?? true,
    included_duration_minutes: pricing.service?.included_duration_minutes ?? '',
    free_buffer_minutes: pricing.service?.free_buffer_minutes ?? 0,
    billing_block_minutes: pricing.extra_time?.billing_block_minutes ?? '',
    price_per_block: pricing.extra_time?.price_per_block ?? '',
    convenience_fee_type: pricing.convenience_fee?.type || 'fixed',
    convenience_fee_value: pricing.convenience_fee?.value ?? '',
    commission_type: pricing.commission?.type || 'percentage',
    commission_value: pricing.commission?.value ?? '',
    tax_type: pricing.tax?.type || 'percentage',
    tax_value: pricing.tax?.value ?? '',
    booking_requirements: {
      case_types_enabled: booking.case_types_enabled || [],
      duration_options: durationOptions.length ? durationOptions : DEFAULT_DURATION_OPTIONS,
      max_duration_minutes_critical: booking.max_duration_minutes_critical ?? 720,
      max_duration_minutes_non_critical: booking.max_duration_minutes_non_critical ?? 43200,
      food_required_from_minutes: booking.food_required_from_minutes ?? 360,
      stay_required_from_minutes: booking.stay_required_from_minutes ?? 1440,
    },
  };
}

export function defaultBookingRequirementsForm() {
  return pricingFormFromConfig({ service_pricing_mode: 'duration_based' }).booking_requirements;
}
